<!-- ============ Documento legal (formato cotizacion.html) ============ -->
<div class="text-[13px] text-gray-800 dark:text-gray-200">

  <!-- HEADER: logo + título + número + meta + badge validez -->
  <div class="flex items-start justify-between gap-4">
    <div class="flex items-center gap-3 min-w-0">
      @if (data.logoUrl) {
        <img [src]="data.logoUrl" alt="Logo" class="h-12 max-w-[180px] object-contain">
      } @else {
        <div class="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
          [style.background-color]="data.accentColor">
          <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="13,2 4,14 11,14 11,22 20,10 13,10"/>
          </svg>
        </div>
      }
      @if (data.providerName) {
        <span class="text-sm font-bold text-gray-900 dark:text-white truncate">{{ data.providerName }}</span>
      }
    </div>

    <div class="text-right flex flex-col items-end gap-1 shrink-0">
      <div class="text-xl sm:text-2xl font-bold tracking-wide" [style.color]="data.accentColor">COTIZACIÓN</div>
      <div class="text-xs text-gray-500 dark:text-gray-400">{{ data.quoteNumber }}@if (!data.isPublic) { (v{{ data.version }})}</div>
      <dl class="mt-1 space-y-0.5 text-[11px]">
        <div class="flex justify-end gap-2">
          <dt class="text-gray-400">Fecha de emisión:</dt>
          <dd class="text-gray-700 dark:text-gray-200 font-medium">{{ formatFecha(data.datetimeIssued) }}</dd>
        </div>
        <div class="flex justify-end gap-2">
          <dt class="text-gray-400">Válida hasta:</dt>
          <dd class="text-gray-700 dark:text-gray-200 font-medium">{{ formatFecha(data.datetimeExpires) }}</dd>
        </div>
        <div class="flex justify-end gap-2">
          <dt class="text-gray-400">Moneda:</dt>
          <dd class="text-gray-700 dark:text-gray-200 font-medium">{{ data.currencyLabel }}</dd>
        </div>
      </dl>
      @if (data.validityDays) {
        <span class="mt-1 inline-block rounded px-3 py-1 text-[11px] font-semibold text-white bg-success-600">
          Válido por {{ data.validityDays }} días
        </span>
      }
      <span class="mt-1 inline-flex items-center px-3 py-1 rounded-full text-[11px] font-medium text-white"
        [style.background-color]="data.accentColor">
        {{ data.statusName }}
      </span>
    </div>
  </div>

  <!-- Regla azul -->
  <div class="h-0.5 mt-3 mb-4" [style.background-color]="data.accentColor"></div>

  <!-- (INC31) Encabezado configurado en la plantilla -->
  @if (data.headerText) {
    <p class="mb-4 text-center text-[12px] text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">{{ data.headerText }}</p>
  }

  <!-- Mensaje de apertura del vendedor -->
  @if (data.openingMessage) {
    <div class="mb-4 rounded-lg bg-gray-50 dark:bg-gray-700/40 border-l-4 px-4 py-3"
      [style.border-left-color]="data.accentColor">
      <p class="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line italic">{{ data.openingMessage }}</p>
      @if (data.providerName) {
        <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">— {{ data.providerName }}</p>
      }
    </div>
  }

  <!-- PARTES: Proveedor | Cliente -->
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
    <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
      <div class="text-[11px] font-bold uppercase tracking-wide mb-0.5" [style.color]="data.secondaryColor">Proveedor</div>
      <div class="text-sm font-bold text-gray-900 dark:text-white">{{ data.providerName || '—' }}</div>
      <div class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-1 space-y-0.5">
        @if (data.providerTaxId) { <div>RUC: {{ data.providerTaxId }}</div> }
        @if (data.providerAddress) { <div class="whitespace-pre-line">{{ data.providerAddress }}</div> }
        @if (data.providerEmail) { <div>{{ data.providerEmail }}</div> }
        @if (data.providerPhone) { <div>{{ data.providerPhone }}</div> }
        <!-- (INC31) Bloque de contacto definido en la plantilla (puede ser multilínea) -->
        @if (data.contactInfo) { <div class="whitespace-pre-line">{{ data.contactInfo }}</div> }
      </div>
    </div>
    <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
      <div class="text-[11px] font-bold uppercase tracking-wide mb-0.5" [style.color]="data.secondaryColor">Cliente</div>
      <!-- Cliente = NOMBRE DEL CONTACTO PRINCIPAL (primario) + empresa (secundaria). Nunca el nombre del negocio. -->
      <div class="text-sm font-bold text-gray-900 dark:text-white">{{ data.clientContactName || data.clientCompanyName || '—' }}</div>
      <div class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-1 space-y-0.5">
        @if (data.clientCompanyName && data.clientCompanyName !== data.clientContactName) { <div>{{ data.clientCompanyName }}</div> }
        @if (data.clientTaxId) { <div>RUC: {{ data.clientTaxId }}</div> }
        @if (data.clientEmail) { <div>{{ data.clientEmail }}</div> }
        @if (data.clientPhone) { <div>{{ data.clientPhone }}</div> }
        @if (data.clientAddress) { <div class="whitespace-pre-line">{{ data.clientAddress }}</div> }
      </div>
    </div>
  </div>

  <!-- TABLA de ítems con filas zebra (V75: + Código + Impuesto; V76: columna Código opcional + agrupado por categoría) -->
  <div class="overflow-x-auto mb-1">
    <table class="w-full border-collapse text-[12px]">
      <thead>
        <tr [style.background-color]="data.accentColor">
          <th class="text-white font-semibold py-2 px-2 text-center">#</th>
          @if (showCodes) {
            <th class="text-white font-semibold py-2 px-2 text-left">Código</th>
          }
          <th class="text-white font-semibold py-2 px-2 text-left">Producto / Servicio</th>
          <th class="text-white font-semibold py-2 px-2 text-left">Descripción</th>
          <th class="text-white font-semibold py-2 px-2 text-center">Cantidad</th>
          <th class="text-white font-semibold py-2 px-2 text-center">Unidad</th>
          <th class="text-white font-semibold py-2 px-2 text-right">Precio Unitario</th>
          <th class="text-white font-semibold py-2 px-2 text-center">Descuento</th>
          <th class="text-white font-semibold py-2 px-2 text-center">Impuesto</th>
          <th class="text-white font-semibold py-2 px-2 text-right">Total</th>
        </tr>
      </thead>

      @if (showGroupedByCategory) {
        <!-- Agrupado por categoría (V76): un subencabezado por categoría.
             (Incidente 14) Solo cuando existe al menos una categoría real; si ninguna, cae a la tabla plana. -->
        @for (group of itemGroups; track group.category) {
          <tbody>
            <tr>
              <td [attr.colspan]="columnCount"
                class="border border-gray-200 dark:border-gray-700 py-1.5 px-3 text-left font-bold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700/60 uppercase tracking-wide text-[11px]">
                {{ group.category }}
              </td>
            </tr>
            @for (item of group.items; track item.id; let idx = $index) {
              <tr class="align-top"
                [class.bg-white]="idx % 2 === 0"
                [class.dark:bg-gray-800]="idx % 2 === 0"
                [class.bg-blue-50]="idx % 2 !== 0"
                [class.dark:bg-gray-900/40]="idx % 2 !== 0">
                <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-center text-gray-700 dark:text-gray-300">{{ item.itemOrder || idx + 1 }}</td>
                @if (showCodes) {
                  <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-left text-gray-600 dark:text-gray-400 whitespace-nowrap">{{ item.itemCode || '—' }}</td>
                }
                <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-left text-gray-900 dark:text-white font-medium">{{ item.itemName }}</td>
                <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-left text-gray-600 dark:text-gray-400 whitespace-pre-line">{{ item.description || '—' }}</td>
                <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-center text-gray-700 dark:text-gray-300">{{ item.quantity | number:'1.2-2' }}</td>
                <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-center text-gray-700 dark:text-gray-300">{{ item.unit || 'Servicio' }}</td>
                <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{{ data.currencySymbol }} {{ item.unitPrice | number:'1.2-2' }}</td>
                <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-center text-gray-700 dark:text-gray-300">
                  @if (item.discountPercent) { {{ item.discountPercent | number:'1.2-2' }}% }
                  @else if (item.discountAmount) { {{ data.currencySymbol }} {{ item.discountAmount | number:'1.2-2' }} }
                  @else { 0.00% }
                </td>
                <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{{ itemTaxLabel(item) }}</td>
                <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-right text-gray-900 dark:text-white font-medium whitespace-nowrap">{{ data.currencySymbol }} {{ item.subtotal | number:'1.2-2' }}</td>
              </tr>
            }
          </tbody>
        }
      } @else {
        <!-- Tabla plana -->
        <tbody>
          @for (item of data.items; track item.id; let idx = $index) {
            <tr class="align-top"
              [class.bg-white]="idx % 2 === 0"
              [class.dark:bg-gray-800]="idx % 2 === 0"
              [class.bg-blue-50]="idx % 2 !== 0"
              [class.dark:bg-gray-900/40]="idx % 2 !== 0">
              <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-center text-gray-700 dark:text-gray-300">{{ item.itemOrder || idx + 1 }}</td>
              @if (showCodes) {
                <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-left text-gray-600 dark:text-gray-400 whitespace-nowrap">{{ item.itemCode || '—' }}</td>
              }
              <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-left text-gray-900 dark:text-white font-medium">{{ item.itemName }}</td>
              <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-left text-gray-600 dark:text-gray-400 whitespace-pre-line">{{ item.description || '—' }}</td>
              <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-center text-gray-700 dark:text-gray-300">{{ item.quantity | number:'1.2-2' }}</td>
              <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-center text-gray-700 dark:text-gray-300">{{ item.unit || 'Servicio' }}</td>
              <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{{ data.currencySymbol }} {{ item.unitPrice | number:'1.2-2' }}</td>
              <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-center text-gray-700 dark:text-gray-300">
                @if (item.discountPercent) { {{ item.discountPercent | number:'1.2-2' }}% }
                @else if (item.discountAmount) { {{ data.currencySymbol }} {{ item.discountAmount | number:'1.2-2' }} }
                @else { 0.00% }
              </td>
              <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{{ itemTaxLabel(item) }}</td>
              <td class="border border-gray-200 dark:border-gray-700 py-2 px-2 text-right text-gray-900 dark:text-white font-medium whitespace-nowrap">{{ data.currencySymbol }} {{ item.subtotal | number:'1.2-2' }}</td>
            </tr>
          }
        </tbody>
      }
    </table>
  </div>
  <p class="text-[11px] italic text-gray-400 mb-4">
    * Los precios indicados pueden variar según la disponibilidad de recursos y condiciones de entrega.
  </p>

  <!-- TOTALES -->
  <div class="flex justify-end mb-5">
    <dl class="w-full sm:w-64 text-[12px] space-y-1">
      <div class="flex justify-between text-gray-500 dark:text-gray-400">
        <dt>Subtotal</dt>
        <dd class="text-gray-800 dark:text-gray-200">{{ data.currencySymbol }} {{ data.subtotal | number:'1.2-2' }}</dd>
      </div>
      @if (data.taxEnabled && data.tax) {
        <div class="flex justify-between text-gray-500 dark:text-gray-400">
          <dt>{{ data.taxLabel || 'IGV' }} ({{ data.taxRatePercent }}%)</dt>
          <dd class="text-gray-800 dark:text-gray-200">{{ data.currencySymbol }} {{ data.tax | number:'1.2-2' }}</dd>
        </div>
      }
      <div class="flex justify-between text-gray-500 dark:text-gray-400">
        <dt>Descuento Global</dt>
        <dd class="text-gray-800 dark:text-gray-200">{{ data.currencySymbol }} {{ (data.globalDiscount || 0) | number:'1.2-2' }}</dd>
      </div>
      <div class="h-0.5 my-1" [style.background-color]="data.accentColor"></div>
      <div class="flex justify-between items-center px-2 py-1.5 rounded text-base font-bold"
        [style.background-color]="data.secondaryColor + '1a'">
        <dt [style.color]="data.accentColor">TOTAL</dt>
        <dd [style.color]="data.accentColor">{{ data.currencySymbol }} {{ data.totalAmount | number:'1.2-2' }}</dd>
      </div>
      <!-- (V188) Total convertido, justo debajo del TOTAL y con la moneda de conversión
           identificada por nombre, código y símbolo. Mismo renglón que imprime el PDF. -->
      @if (convertedTotal != null) {
        <div class="flex justify-between gap-2 text-gray-500 dark:text-gray-400">
          <dt>{{ convertedTotalLabel }}</dt>
          <dd class="text-gray-800 dark:text-gray-200 whitespace-nowrap">{{ convertedTotalSymbol }}{{ convertedTotal | number:'1.2-2' }}</dd>
        </div>
      }
    </dl>
  </div>

  <!-- TÉRMINOS | CLÁUSULAS -->
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
    <div class="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div class="bg-gray-100 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-700 px-3 py-2 text-[12px] font-bold text-gray-900 dark:text-white">
        Términos y Condiciones
      </div>
      <div class="px-3 py-2.5 bg-gray-50 dark:bg-gray-900/40 text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line">
        @if (data.termsConditions) {
          {{ data.termsConditions }}
        } @else {
          Al aceptar esta cotización, el cliente confirma haber leído y aceptado los términos y condiciones adjuntos.
        }
      </div>
    </div>
    <div class="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div class="bg-gray-100 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-700 px-3 py-2 text-[12px] font-bold text-gray-900 dark:text-white">
        Cláusulas Incluidas
      </div>
      <div class="px-3 py-2.5 bg-gray-50 dark:bg-gray-900/40 text-[11px] text-gray-700 dark:text-gray-300 space-y-1">
        @if (data.clauses.length > 0) {
          @for (clause of data.clauses; track clause.id) {
            <div class="flex items-start gap-1.5">
              <span class="text-success-600 font-bold leading-tight">✓</span>
              <span>{{ clause.name }}</span>
            </div>
          }
        } @else {
          <p class="text-gray-400 italic">Sin cláusulas adicionales.</p>
        }
      </div>
    </div>
  </div>

  <!-- CONDICIONES COMERCIALES (V74) — solo los campos con valor -->
  @if (hasCommercialConditions) {
    <div class="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-5">
      <div class="bg-gray-100 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-700 px-3 py-2 text-[12px] font-bold text-gray-900 dark:text-white">
        Condiciones Comerciales
      </div>
      <dl class="px-3 py-2.5 bg-gray-50 dark:bg-gray-900/40 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]">
        @if (paymentLabel) {
          <div class="flex justify-between gap-2">
            <dt class="text-gray-500 dark:text-gray-400">Forma de pago</dt>
            <dd class="text-gray-800 dark:text-gray-200 font-medium text-right">{{ paymentLabel }}</dd>
          </div>
        }
        @if (data.advancePercent != null) {
          <div class="flex justify-between gap-2">
            <dt class="text-gray-500 dark:text-gray-400">Anticipo</dt>
            <dd class="text-gray-800 dark:text-gray-200 font-medium text-right">{{ data.advancePercent | number:'1.0-2' }}%</dd>
          </div>
        }
        @if (data.exchangeRate != null) {
          <div class="flex justify-between gap-2">
            <dt class="text-gray-500 dark:text-gray-400">Tipo de cambio</dt>
            <dd class="text-gray-800 dark:text-gray-200 font-medium text-right">{{ data.exchangeRate | number:'1.2-4' }}</dd>
          </div>
        }
        @if (data.deliveryTime) {
          <div class="flex justify-between gap-2">
            <dt class="text-gray-500 dark:text-gray-400">Plazo de entrega</dt>
            <dd class="text-gray-800 dark:text-gray-200 font-medium text-right whitespace-pre-line">{{ data.deliveryTime }}</dd>
          </div>
        }
        @if (data.deliveryPlace) {
          <div class="flex justify-between gap-2">
            <dt class="text-gray-500 dark:text-gray-400">Lugar de entrega</dt>
            <dd class="text-gray-800 dark:text-gray-200 font-medium text-right whitespace-pre-line">{{ data.deliveryPlace }}</dd>
          </div>
        }
        @if (data.warranty) {
          <div class="flex justify-between gap-2 sm:col-span-2">
            <dt class="text-gray-500 dark:text-gray-400">Garantía</dt>
            <dd class="text-gray-800 dark:text-gray-200 font-medium text-right whitespace-pre-line">{{ data.warranty }}</dd>
          </div>
        }
      </dl>

      <!-- (Incidente 15) Desglose de pago a crédito: anticipo, saldo financiado, intereses, total con financiamiento y total convertido -->
      @if (showAdvanceBreakdown || showCreditBreakdown || showFinancingBreakdown) {
        <div class="border-t border-gray-200 dark:border-gray-700 px-3 py-2.5 bg-gray-50 dark:bg-gray-900/40">
          <div class="text-[11px] font-bold text-gray-700 dark:text-gray-200 mb-1.5">{{ breakdownTitle }}</div>
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[11px]">
            @if (data.advancePercent != null) {
              <div class="flex justify-between gap-2">
                <dt class="text-gray-500 dark:text-gray-400">Anticipo ({{ data.advancePercent | number:'1.0-2' }}%)</dt>
                <dd class="text-gray-800 dark:text-gray-200 font-medium text-right whitespace-nowrap">− {{ data.currencySymbol }} {{ advanceAmount | number:'1.2-2' }}</dd>
              </div>
            }
            @if (showFinancingBreakdown) {
              <div class="flex justify-between gap-2">
                <dt class="text-gray-500 dark:text-gray-400">Saldo financiado @if (data.creditTermDays != null) { <span class="text-gray-400">(a {{ data.creditTermDays }} días)</span> }</dt>
                <dd class="text-gray-800 dark:text-gray-200 font-medium text-right whitespace-nowrap">{{ data.currencySymbol }} {{ financedBalance | number:'1.2-2' }}</dd>
              </div>
              <div class="flex justify-between gap-2">
                <dt class="text-gray-500 dark:text-gray-400">Intereses ({{ data.interestRatePercent | number:'1.0-2' }}%)</dt>
                <dd class="text-gray-800 dark:text-gray-200 font-medium text-right whitespace-nowrap">{{ data.currencySymbol }} {{ interestAmount | number:'1.2-2' }}</dd>
              </div>
              <div class="flex justify-between gap-2 sm:col-span-2">
                <dt class="text-gray-700 dark:text-gray-200 font-semibold">Total con financiamiento</dt>
                <dd class="font-bold text-right whitespace-nowrap" [style.color]="data.accentColor">{{ data.currencySymbol }} {{ totalWithFinancing | number:'1.2-2' }}</dd>
              </div>
            } @else {
              <div class="flex justify-between gap-2 sm:col-span-2">
                <dt class="text-gray-700 dark:text-gray-200 font-semibold">Saldo pendiente @if (data.creditTermDays != null) { <span class="font-normal text-gray-400">(a {{ data.creditTermDays }} días)</span> }</dt>
                <dd class="font-bold text-right whitespace-nowrap" [style.color]="data.accentColor">{{ data.currencySymbol }} {{ financedBalance | number:'1.2-2' }}</dd>
              </div>
            }
          </dl>
        </div>
      }
    </div>
  }

  <!-- DOBLE FIRMA: Responsable | Cliente -->
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
    <!-- Firma del Responsable -->
    <div>
      <div class="text-[12px] font-bold text-gray-900 dark:text-white mb-1.5">Firma del Responsable</div>
      <div class="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-3 min-h-[120px]">
        @if (data.approverSignature; as sig) {
          @if (isImageSignature(sig)) {
            <img [src]="sig.signatureData" alt="Firma" class="h-12 object-contain mb-1">
          } @else {
            <div class="text-2xl mb-1 italic" [style.color]="data.accentColor"
              style="font-family: 'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive;">
              {{ sig.signatureData || sig.signatoryName }}
            </div>
          }
          <div class="h-px bg-gray-200 dark:bg-gray-700 my-1.5"></div>
          <div class="text-[12px] font-bold text-gray-900 dark:text-white">{{ sig.signatoryName }}</div>
          <div class="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
            @if (sig.signatoryRole) { <div>{{ sig.signatoryRole }}</div> }
            @if (data.providerName) { <div>{{ data.providerName }}</div> }
          </div>
          <div class="mt-2 flex items-center gap-2 rounded bg-success-50 dark:bg-success-500/10 border border-success-200 dark:border-success-500/30 px-2 py-1.5">
            <span class="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-success-600 text-white text-[9px] font-bold">✓</span>
            <div class="text-[10px] leading-tight">
              <strong class="block text-gray-900 dark:text-white">Firmado electrónicamente</strong>
              <span class="text-gray-500 dark:text-gray-400">{{ formatFechaHora(sig.datetimeSigned) }}</span>
              @if (sig.signatoryIp) { <span class="block text-gray-500 dark:text-gray-400">IP: {{ sig.signatoryIp }}</span> }
            </div>
          </div>
        } @else {
          <div class="h-px bg-gray-200 dark:bg-gray-700 mt-10 mb-1.5"></div>
          <p class="text-[11px] text-gray-400 italic text-center">Pendiente de firma</p>
        }
      </div>
    </div>

    <!-- Firma del Cliente -->
    <div>
      <div class="text-[12px] font-bold text-gray-900 dark:text-white mb-1.5">Firma del Cliente</div>
      <div class="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-3 min-h-[120px]">
        @if (data.clientSignature; as sig) {
          @if (isImageSignature(sig)) {
            <img [src]="sig.signatureData" alt="Firma" class="h-12 object-contain mb-1">
          } @else {
            <div class="text-2xl mb-1 italic" [style.color]="data.accentColor"
              style="font-family: 'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive;">
              {{ sig.signatureData || sig.signatoryName }}
            </div>
          }
          <div class="h-px bg-gray-200 dark:bg-gray-700 my-1.5"></div>
          <div class="text-[12px] font-bold text-gray-900 dark:text-white">{{ sig.signatoryName }}</div>
          <div class="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
            @if (sig.signatoryRole) { <div>{{ sig.signatoryRole }}</div> }
            @if (data.clientCompanyName) { <div>{{ data.clientCompanyName }}</div> }
          </div>
          <div class="mt-2 flex items-center gap-2 rounded bg-success-50 dark:bg-success-500/10 border border-success-200 dark:border-success-500/30 px-2 py-1.5">
            <span class="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-success-600 text-white text-[9px] font-bold">✓</span>
            <div class="text-[10px] leading-tight">
              <strong class="block text-gray-900 dark:text-white">Firmado electrónicamente</strong>
              <span class="text-gray-500 dark:text-gray-400">{{ formatFechaHora(sig.datetimeSigned) }}</span>
              @if (sig.signatoryIp) { <span class="block text-gray-500 dark:text-gray-400">IP: {{ sig.signatoryIp }}</span> }
            </div>
          </div>
        } @else {
          <div class="h-px bg-gray-200 dark:bg-gray-700 mt-10 mb-1.5"></div>
          <p class="text-[11px] text-gray-400 italic text-center">Pendiente de firma</p>
        }
      </div>
    </div>
  </div>

  <!-- Notas (si hay) -->
  @if (data.notes) {
    <div class="mt-4">
      <h3 class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Notas</h3>
      <p class="text-[12px] text-gray-600 dark:text-gray-300 whitespace-pre-line">{{ data.notes }}</p>
    </div>
  }

  <!-- FOOTER legal -->
  <div class="h-px bg-gray-200 dark:bg-gray-700 mt-5 mb-3"></div>
  <div class="text-center text-[10px] text-gray-400 leading-relaxed">
    Este documento tiene validez legal según la Ley N° 27269 y su Reglamento.<br>
    Documento firmado electrónicamente con plena validez jurídica.
    @if (data.documentHash) {
      <br><span class="break-all">Hash (SHA-256): {{ data.documentHash }}</span>
    }
    @if (data.templateFooterText) {
      <br><span class="whitespace-pre-line">{{ data.templateFooterText }}</span>
    }
  </div>
</div>
